import dayjs from 'dayjs';
import { i18n } from 'i18next';

export type SupportedLanguage =
  | 'ca'
  | 'cs'
  | 'da'
  | 'de'
  | 'el'
  | 'en_gb'
  | 'en_us'
  | 'es_419'
  | 'es_es'
  | 'eu'
  | 'fi'
  | 'fr_fr'
  | 'gl'
  | 'it'
  | 'nl'
  | 'no'
  | 'pl'
  | 'pt_br'
  | 'pt_pt'
  | 'ro'
  | 'ru'
  | 'sv'
  | 'tr';

const LANGUAGE_STORAGE_KEY = 'foco_acredita_language';
const DEFAULT_LANGUAGE: SupportedLanguage = 'es_es';

export const getStoredLanguage = (): SupportedLanguage => {
  if (typeof window === 'undefined') {
    return DEFAULT_LANGUAGE;
  }

  try {
    const stored = localStorage.getItem(LANGUAGE_STORAGE_KEY);
    if (stored && isValidLanguage(stored)) {
      return stored as SupportedLanguage;
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error reading language from localStorage:', error);
  }

  return DEFAULT_LANGUAGE;
};

const setStoredLanguage = (language: SupportedLanguage): void => {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    localStorage.setItem(LANGUAGE_STORAGE_KEY, language);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error saving language to localStorage:', error);
  }
};

const isValidLanguage = (language: string): boolean => {
  const validLanguages: SupportedLanguage[] = [
    'ca',
    'cs',
    'da',
    'de',
    'el',
    'en_gb',
    'en_us',
    'es_419',
    'es_es',
    'eu',
    'fi',
    'fr_fr',
    'gl',
    'it',
    'nl',
    'no',
    'pl',
    'pt_br',
    'pt_pt',
    'ro',
    'ru',
    'sv',
    'tr',
  ];

  return validLanguages.includes(language as SupportedLanguage);
};

export const getTranslation = async (language: SupportedLanguage) => {
  const files = ['general.js'];

  const promises = files.map((e) => import(/* @vite-ignore */ `/translations/${language}/${e}`));
  const results = await Promise.allSettled(promises);
  let translation = {};
  results.forEach((e) => {
    if (e.status === 'rejected') {
      // eslint-disable-next-line no-console
      console.error('Error loading translation', e.reason);
    } else {
      translation = { ...translation, ...e.value.default };
    }
  });

  return translation;
};

export const loadAndChangeLanguage = async (i18n: i18n, language: SupportedLanguage) => {
  const isLanguageLoaded = i18n.hasResourceBundle(language, 'translation');
  if (!isLanguageLoaded) {
    const translation = await getTranslation(language);
    i18n.addResourceBundle(language, 'translation', translation);
  }
  i18n.changeLanguage(language);
  dayjs.locale(language.slice(0, 2));
  setStoredLanguage(language);
};
